import React, { useCallback, useEffect, useState } from "react";
import { ApexOptions } from "apexcharts";
import { IoMdDownload } from "react-icons/io";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";
import DateRangePicker from "../../molecules/DateRangePicker";
import {
  CacheWithExpiration,
  getDateBeforeXDays,
  getISODateFromDate,
} from "../../../lib/utils/funcs";
import {
  RCCountAnalyticsResponse,
  DownloadRequestResponse,
} from "../../../lib/utils/types";
import {
  getRescheduledAndCancelledAnalytics,
  downloadCancellationRescheduleData,
} from "../../../lib/apis/analytics";
import ChartError from "../../molecules/ChartError";
import ReactApexChart from "react-apexcharts";
import ChartLoader from "../../molecules/ChartLoader";
import { format } from 'date-fns';
import { useUserData } from "../../../lib/contexts/UserContext";
import { toast } from "react-toastify";

interface CancellationsAndRescheduledAreaChartProps {
  hospitalId?: string;
  startDate?: string;
  endDate?: string;
  report?:boolean;
  rcStats?: any;
  onDownloadRequested?: (response: DownloadRequestResponse) => void;
}

function CancellationsAndRescheduledAreaChart({ hospitalId, startDate, endDate, report, rcStats, onDownloadRequested }: CancellationsAndRescheduledAreaChartProps) {
  const defaultData = {
    count_data: [],
    totals: {
      total_cancelled: 0,
      total_rescheduled: 0
    }
  }
  const { userData } = useUserData();
  const { hospitalID: contextHospitalID } = useHospDocData();
  const effectiveHospitalId = hospitalId || contextHospitalID;
  // If props provided, use them, else use internal state
  const [internalStartDate, setInternalStartDate] = useState<Date>(getDateBeforeXDays(30));
  const [internalEndDate, setInternalEndDate] = useState<Date>(getDateBeforeXDays(1));
  const effectiveStartDate = startDate ? new Date(startDate) : internalStartDate;
  const effectiveEndDate = endDate ? new Date(endDate) : internalEndDate;
  const [rescheduledCancelledData, setRescheduledCancelledData] = useState<RCCountAnalyticsResponse>(defaultData);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Update handlers to use internal state only if not in external mode
  const isExternal = !!(hospitalId && startDate && endDate);
  const handleStartDateChange = (date: Date | null) => {
    if (!isExternal && date) setInternalStartDate(date);
  };
  const handleEndDateChange = (date: Date | null) => {
    if (!isExternal && date) setInternalEndDate(date);
  };

  const handleRetry = () => {
    fetchCancelReasonDataWithCache();
  };

  const fetchCancelReasonDataWithCache = useCallback(async () => {
    if (!effectiveHospitalId) return;
    setError(null);
    const loadingTimeout = setTimeout(() => {
      setIsLoading(true);
    }, 500);
    const cacheKey = `RC-Count-${effectiveHospitalId}-${getISODateFromDate(effectiveStartDate)}-${getISODateFromDate(effectiveEndDate)}`;
    try {
      // Check cache first
      const cachedData = CacheWithExpiration.get(cacheKey);
      if (cachedData) {
        console.log("Using cached rescheduled and cancelled data: 🗄️");
        setRescheduledCancelledData(cachedData);
        clearTimeout(loadingTimeout);
        setIsLoading(false);
        return;
      }
      const data = await getRescheduledAndCancelledAnalytics({
        hospital_id: effectiveHospitalId,
        start_date: getISODateFromDate(effectiveStartDate),
        end_date: getISODateFromDate(effectiveEndDate),
        doctor_id:userData?.doctor_id
      });
      console.log("Fetched rescheduled and cancelled data: 🛜");
      CacheWithExpiration.set(cacheKey, data, 60);
      setRescheduledCancelledData(data);
    } catch (error) {
      setError("Failed to load chart data. Please try again.");
      console.error("Failed to fetch rescheduled and cancelled data:", error);
    } finally {
      clearTimeout(loadingTimeout);
      setIsLoading(false);
    }
  }, [effectiveHospitalId, effectiveStartDate, effectiveEndDate, userData]);

  const handleDownloadCancellationReschedule = async () => {
    if (!effectiveHospitalId) return;
    const params = {
      hospital_id: effectiveHospitalId,
      start_date: format(effectiveStartDate, 'yyyy-MM-dd'),
      end_date: format(effectiveEndDate, 'yyyy-MM-dd'),
      doctor_id: userData?.doctor_id
    };
    try {
      const response = await downloadCancellationRescheduleData(params);
      if (onDownloadRequested) {
        onDownloadRequested(response);
      } else {
        toast.success("Download request submitted successfully");
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to request download");
    }
  };

  // Fetch data
  useEffect(() => {
    if (rcStats) {
      setRescheduledCancelledData(rcStats);
    } else if (userData && effectiveHospitalId && effectiveStartDate && effectiveEndDate) {
      fetchCancelReasonDataWithCache();
    }
  }, [effectiveHospitalId, effectiveStartDate, effectiveEndDate, userData, rcStats, fetchCancelReasonDataWithCache]);

  const areaChartOptions: ApexOptions = {
    chart: {
      type: "area",
      height: 350,
      toolbar: {
        show: false,
      },
    },
    colors: ["#81B2F9", "#4AD991"], // Blue and Green from the image
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    xaxis: {
      categories: rescheduledCancelledData.count_data?.map((item) => item.timeKey),
      labels: {
        style: {
          colors: "#A0AEC0", // Light gray for labels
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#A0AEC0", // Light gray for labels
        },
      },
    },
    tooltip: {
      x: {
        format: "HH:mm",
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
  };

  const areaChartSeries = [
    {
      name: "Cancelled",
      data: rescheduledCancelledData.count_data?.map((item) => item.cancelled),
    },
    {
      name: "Rescheduled",
      data: rescheduledCancelledData.count_data?.map((item) => item.rescheduled),
    },
  ];

  return (
    <div className={`w-full ${report ? "w-full" : "lg:w-1/2"} bg-white p-4 rounded-lg shadow`}>
      {/* chart title and date range selector */}
      <div className="flex flex-col gap-2 sm:flex-row justify-between items-center p-2 pt-0 mb-4">
        <h2 className="hidden md:flex text-lg font-semibold text-gray-700 ">
          Cancellations & Reschedules
        </h2>
        <div className="flex md:hidden flex-row justify-between w-full">
        <h2 className="flex text-lg font-semibold text-gray-700 ">
          Cancellations & Reschedules
        </h2>
        <IoMdDownload className="flex w-8 h-8 text-[#474747] hover:bg-gray-300 p-1 rounded-md cursor-pointer" onClick={handleDownloadCancellationReschedule} />
        </div>
        <div className={`flex gap-4 ${report ? "hidden" : ""}`}>
          <DateRangePicker
            startDate={effectiveStartDate}
            endDate={effectiveEndDate}
            onStartChange={handleStartDateChange}
            onEndChange={handleEndDateChange}
            size="md"
            dateFormat="MMM dd, yyyy"
            inputClassName="border-blue-300 focus:ring-blue-600 text-sm py-1"
            separator="to"
            showIcons={false}
            maxDate={getDateBeforeXDays(1)}
            // minDate={getDateBeforeXDays(364)}
          />
          <IoMdDownload className="hidden md:flex w-8 h-8 text-[#474747] hover:bg-gray-300 p-1 rounded-md cursor-pointer" onClick={handleDownloadCancellationReschedule} />
        </div>
      </div>

      {/* Data Loading State */}
      {isLoading && (
        <ChartLoader height={350}  containerClassName="" />
      )}

      {/* Error State */}
      {error && !isLoading && (
        <ChartError
          error={error}
          onRetry={handleRetry}
          containerClassName="border-t border-gray-100"
          height={350}
        />
      )}

      {/* Chart Content */}
      {!isLoading && !error && (
        <ReactApexChart
          options={areaChartOptions}
          series={areaChartSeries}
          type="area"
          height={350}
        />
      )}
    </div>
  );
}

export default CancellationsAndRescheduledAreaChart;
